import { z } from 'zod';

const optionalText = (max: number) =>
	z.preprocess(
		(value) => (value === '' ? undefined : value),
		z.string().trim().min(1).max(max).optional(),
	);

const httpsUrl = z
	.url()
	.max(2048)
	.refine((value) => new URL(value).protocol === 'https:', 'Only HTTPS URLs are allowed');

/**
 * Business-card logos may be supplied as a hosted HTTPS image or a small image chosen in the
 * creator. Keeping embedded images below 1 MB ensures their base64 representation remains well
 * within the API's 3 MB JSON body limit.
 */
const businessCardLogoUrl = z.union([
	httpsUrl,
	z
		.string()
		.regex(
			/^data:image\/(?:png|jpeg|webp|svg\+xml);base64,[A-Za-z0-9+/]+={0,2}$/,
			'Logo must be a PNG, JPEG, WebP, or SVG image',
		)
		.max(1_400_000, 'Logo image must be 1 MB or smaller'),
]);

const phone = z
	.string()
	.trim()
	.regex(/^\+?[0-9()\- .]{7,24}$/, 'Invalid phone number')
	.transform((value) => value.replace(/[()\- .]/g, ''));

export const QR_EXPERIENCE_TYPES = [
	'business-card',
	'social-links',
	'menu',
	'pdf',
	'coupon',
	'app',
] as const;
export const QrExperienceTypeSchema = z.enum(QR_EXPERIENCE_TYPES);
export type TQrExperienceType = z.infer<typeof QrExperienceTypeSchema>;

export const QR_EXPERIENCE_STATUSES = ['draft', 'published', 'archived'] as const;
export const QrExperienceStatusSchema = z.enum(QR_EXPERIENCE_STATUSES);
export type TQrExperienceStatus = z.infer<typeof QrExperienceStatusSchema>;

export const QR_EXPERIENCE_THEMES = ['glass', 'dark', 'light', 'minimal'] as const;
export const QrExperienceThemeSchema = z.enum(QR_EXPERIENCE_THEMES).default('glass');
export type TQrExperienceTheme = z.infer<typeof QrExperienceThemeSchema>;

export const QR_EXPERIENCE_ACTION_TYPES = [
	'save-contact',
	'call',
	'email',
	'open-map',
	'open-website',
	'open-social',
	'open-whatsapp',
	'view-pdf',
	'download-pdf',
	'copy-coupon',
	'download-app-ios',
	'download-app-android',
] as const;
export const QrExperienceActionTypeSchema = z.enum(QR_EXPERIENCE_ACTION_TYPES);
export type TQrExperienceActionType = z.infer<typeof QrExperienceActionTypeSchema>;

export const SOCIAL_PLATFORMS = [
	'website',
	'instagram',
	'facebook',
	'linkedin',
	'tiktok',
	'youtube',
	'x',
	'threads',
	'snapchat',
	'pinterest',
	'spotify',
	'whatsapp',
	'telegram',
	'github',
	'gitlab',
	'dribbble',
	'behance',
	'discord',
	'twitch',
	'reddit',
	'tumblr',
	'vimeo',
	'vk',
	'xing',
	'messenger',
	'yelp',
	'tripadvisor',
	'ubereats',
	'doordash',
	'soundcloud',
	'wechat',
	'line',
	'flickr',
	'medium',
	'custom',
] as const;
export const SocialPlatformSchema = z.enum(SOCIAL_PLATFORMS);
export type TExperienceSocialPlatform = z.infer<typeof SocialPlatformSchema>;

export const SocialExperienceLinkSchema = z.object({
	platform: SocialPlatformSchema,
	label: optionalText(60),
	url: httpsUrl,
});
export type TSocialExperienceLink = z.infer<typeof SocialExperienceLinkSchema>;

export const OpeningHourItemSchema = z.object({
	day: z.string().trim().max(20),
	open: z.string().trim().max(10),
	close: z.string().trim().max(10),
	isClosed: z.boolean().default(false),
});
export type TOpeningHourItem = z.infer<typeof OpeningHourItemSchema>;

export const BusinessCardExperienceContentSchema = z.object({
	type: z.literal('business-card'),
	fullName: z.string().trim().min(1).max(120),
	role: optionalText(100),
	company: optionalText(120),
	email: z.preprocess((value) => (value === '' ? undefined : value), z.email().max(320).optional()),
	phone: z.preprocess((value) => (value === '' ? undefined : value), phone.optional()),
	whatsApp: z.preprocess((value) => (value === '' ? undefined : value), phone.optional()),
	website: z.preprocess((value) => (value === '' ? undefined : value), httpsUrl.optional()),
	address: optionalText(240),
	mapUrl: z.preprocess((value) => (value === '' ? undefined : value), httpsUrl.optional()),
	bio: optionalText(1500),
	logoUrl: z.preprocess(
		(value) => (value === '' ? undefined : value),
		businessCardLogoUrl.optional(),
	),
	tagline: optionalText(140),
	about: optionalText(2000),
	openingHours: z.array(OpeningHourItemSchema).max(14).optional(),
	facilities: z.array(z.string().trim().max(50)).max(16).optional(),
	socialLinks: z.array(SocialExperienceLinkSchema).max(30).optional(),
	headerColor: z
		.string()
		.regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid 6-character hex color')
		.optional(),
	accentColor: z
		.string()
		.regex(/^#[0-9A-Fa-f]{6}$/)
		.default('#111827'),
	theme: QrExperienceThemeSchema.optional(),
	coverUrl: z.preprocess(
		(value) => (value === '' ? undefined : value),
		businessCardLogoUrl.optional(),
	),
	leadAlertsEnabled: z.boolean().default(true).optional(),
	leadNotificationEmail: z.preprocess(
		(value) => (value === '' ? undefined : value),
		z.email().max(320).optional(),
	),
	webhookUrl: z.preprocess((value) => (value === '' ? undefined : value), httpsUrl.optional()),
});
export type TBusinessCardExperienceContent = z.infer<typeof BusinessCardExperienceContentSchema>;

export const SocialLinksExperienceContentSchema = z.object({
	type: z.literal('social-links'),
	title: z.string().trim().min(1).max(120),
	subtitle: optionalText(240),
	links: z.array(SocialExperienceLinkSchema).min(1).max(30),
	headerColor: z
		.string()
		.regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid 6-character hex color')
		.optional(),
	accentColor: z
		.string()
		.regex(/^#[0-9A-Fa-f]{6}$/)
		.default('#111827'),
	theme: QrExperienceThemeSchema.optional(),
	coverUrl: z.preprocess(
		(value) => (value === '' ? undefined : value),
		businessCardLogoUrl.optional(),
	),
	leadAlertsEnabled: z.boolean().default(true).optional(),
	leadNotificationEmail: z.preprocess(
		(value) => (value === '' ? undefined : value),
		z.email().max(320).optional(),
	),
	webhookUrl: z.preprocess((value) => (value === '' ? undefined : value), httpsUrl.optional()),
});
export type TSocialLinksExperienceContent = z.infer<typeof SocialLinksExperienceContentSchema>;

export const MenuItemSchema = z.object({
	id: z.string().trim().min(1).max(50),
	name: z.string().trim().min(1).max(100),
	description: optionalText(400),
	price: z.string().trim().min(1).max(20),
	dietary: z.array(z.string().trim().max(30)).max(10).optional(),
	imageUrl: z.preprocess(
		(value) => (value === '' ? undefined : value),
		businessCardLogoUrl.optional(),
	),
	isAvailable: z.boolean().default(true),
});
export type TMenuItem = z.infer<typeof MenuItemSchema>;

export const MenuCategorySchema = z.object({
	id: z.string().trim().min(1).max(50),
	name: z.string().trim().min(1).max(60),
	description: optionalText(200),
	items: z.array(MenuItemSchema).min(1).max(40),
});
export type TMenuCategory = z.infer<typeof MenuCategorySchema>;

export const MenuExperienceContentSchema = z.object({
	type: z.literal('menu'),
	restaurantName: z.string().trim().min(1).max(120),
	tagline: optionalText(140),
	description: optionalText(1000),
	currency: z.string().trim().min(1).max(10).default('$'),
	phone: z.preprocess((value) => (value === '' ? undefined : value), phone.optional()),
	whatsApp: z.preprocess((value) => (value === '' ? undefined : value), phone.optional()),
	website: z.preprocess((value) => (value === '' ? undefined : value), httpsUrl.optional()),
	address: optionalText(240),
	mapUrl: z.preprocess((value) => (value === '' ? undefined : value), httpsUrl.optional()),
	logoUrl: z.preprocess(
		(value) => (value === '' ? undefined : value),
		businessCardLogoUrl.optional(),
	),
	categories: z.array(MenuCategorySchema).min(1).max(20),
	headerColor: z
		.string()
		.regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid 6-character hex color')
		.optional(),
	accentColor: z
		.string()
		.regex(/^#[0-9A-Fa-f]{6}$/)
		.default('#1d4ed8'),
	theme: QrExperienceThemeSchema.optional(),
	coverUrl: z.preprocess(
		(value) => (value === '' ? undefined : value),
		businessCardLogoUrl.optional(),
	),
});
export type TMenuExperienceContent = z.infer<typeof MenuExperienceContentSchema>;

const pdfFileUrl = z.union([
	httpsUrl,
	z
		.string()
		.regex(
			/^data:application\/pdf;base64,[A-Za-z0-9+/]+={0,2}$/,
			'File must be a valid PDF document data URI',
		)
		.max(2_500_000, 'Uploaded PDF must be 1.8 MB or smaller'),
]);

export const PdfExperienceContentSchema = z.object({
	type: z.literal('pdf'),
	title: z.preprocess(
		(value) => (value === '' ? undefined : value),
		z.string().trim().max(120).optional(),
	),
	description: optionalText(1000),
	company: optionalText(120),
	pdfUrl: pdfFileUrl,
	directPdf: z.boolean().default(false),
	fileSize: optionalText(20),
	pageCount: optionalText(20),
	coverUrl: z.preprocess(
		(value) => (value === '' ? undefined : value),
		businessCardLogoUrl.optional(),
	),
	website: z.preprocess((value) => (value === '' ? undefined : value), httpsUrl.optional()),
	email: z.preprocess((value) => (value === '' ? undefined : value), z.email().max(320).optional()),
	phone: z.preprocess((value) => (value === '' ? undefined : value), phone.optional()),
	headerColor: z
		.string()
		.regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid 6-character hex color')
		.optional(),
	accentColor: z
		.string()
		.regex(/^#[0-9A-Fa-f]{6}$/)
		.default('#1d4ed8'),
});
export type TPdfExperienceContent = z.infer<typeof PdfExperienceContentSchema>;

export const CouponExperienceContentSchema = z.object({
	type: z.literal('coupon'),
	company: z.string().trim().min(1).max(120),
	title: z.string().trim().min(1).max(120),
	couponCode: z.string().trim().min(1).max(40),
	discount: optionalText(40),
	validUntil: optionalText(60),
	terms: optionalText(1500),
	website: z.preprocess((value) => (value === '' ? undefined : value), httpsUrl.optional()),
	phone: z.preprocess((value) => (value === '' ? undefined : value), phone.optional()),
	address: optionalText(240),
	headerColor: z
		.string()
		.regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid 6-character hex color')
		.optional(),
	accentColor: z
		.string()
		.regex(/^#[0-9A-Fa-f]{6}$/)
		.default('#1d4ed8'),
});
export type TCouponExperienceContent = z.infer<typeof CouponExperienceContentSchema>;

export const AppExperienceContentSchema = z
	.object({
		type: z.literal('app'),
		appName: z.string().trim().min(1, 'App name is required').max(120),
		developer: optionalText(120),
		description: optionalText(1500),
		logoUrl: businessCardLogoUrl.optional(),
		iosUrl: z.preprocess((value) => (value === '' ? undefined : value), httpsUrl.optional()),
		androidUrl: z.preprocess((value) => (value === '' ? undefined : value), httpsUrl.optional()),
		websiteUrl: z.preprocess((value) => (value === '' ? undefined : value), httpsUrl.optional()),
		headerColor: z
			.string()
			.regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid 6-character hex color')
			.optional(),
		accentColor: z
			.string()
			.regex(/^#[0-9A-Fa-f]{6}$/)
			.default('#1d4ed8'),
	})
	.refine((data) => Boolean(data.iosUrl || data.androidUrl || data.websiteUrl), {
		message: 'Provide at least one store or website URL for your app',
		path: ['iosUrl'],
	});
export type TAppExperienceContent = z.infer<typeof AppExperienceContentSchema>;

export const QrExperienceContentSchema = z.discriminatedUnion('type', [
	BusinessCardExperienceContentSchema,
	SocialLinksExperienceContentSchema,
	MenuExperienceContentSchema,
	PdfExperienceContentSchema,
	CouponExperienceContentSchema,
	AppExperienceContentSchema,
]);
export type TQrExperienceContent = z.infer<typeof QrExperienceContentSchema>;

export const QrExperienceCreateDto = z.object({
	qrCodeId: z.uuid(),
	shortUrlId: z.uuid(),
	content: QrExperienceContentSchema,
});
export type TQrExperienceCreateDto = z.infer<typeof QrExperienceCreateDto>;

export const QrExperienceUpdateDto = z.object({
	status: QrExperienceStatusSchema.optional(),
	content: QrExperienceContentSchema.optional(),
});
export type TQrExperienceUpdateDto = z.infer<typeof QrExperienceUpdateDto>;

export const QrExperienceActionDto = z.object({
	actionType: QrExperienceActionTypeSchema,
	targetPlatform: SocialPlatformSchema.optional(),
	targetLabel: z.string().trim().max(120).optional(),
});
export type TQrExperienceActionDto = z.infer<typeof QrExperienceActionDto>;

export const QrExperienceIdParamsDto = z.object({ id: z.uuid() });
export type TQrExperienceIdParamsDto = z.infer<typeof QrExperienceIdParamsDto>;

export const QrExperienceSlugParamsDto = z.object({
	slug: z.string().regex(/^[a-z0-9][a-z0-9-]{5,63}$/),
});
export type TQrExperienceSlugParamsDto = z.infer<typeof QrExperienceSlugParamsDto>;

export const QrExperiencePublicDto = z.object({
	publicSlug: z.string().regex(/^[a-z0-9][a-z0-9-]{5,63}$/),
	content: QrExperienceContentSchema,
});
export type TQrExperiencePublicDto = z.infer<typeof QrExperiencePublicDto>;

export const QrExperienceOwnerDto = QrExperiencePublicDto.extend({
	id: z.uuid(),
	qrCodeId: z.uuid(),
	shortUrlId: z.uuid(),
	status: QrExperienceStatusSchema,
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().nullable(),
});
export type TQrExperienceOwnerDto = z.infer<typeof QrExperienceOwnerDto>;

export const QrExperienceAnalyticsDto = z.object({
	actions: z.number().int().nonnegative(),
	actionCounts: z.array(
		z.object({
			actionType: QrExperienceActionTypeSchema,
			targetPlatform: SocialPlatformSchema.nullable(),
			targetLabel: z.string().nullable().optional(),
			count: z.number().int().nonnegative(),
		}),
	),
});
export type TQrExperienceAnalyticsDto = z.infer<typeof QrExperienceAnalyticsDto>;

export const CreateExperienceLeadDto = z.object({
	name: z.string().trim().min(1, 'Name is required').max(120),
	email: z.string().trim().email('Valid email is required').max(180),
	phone: z.string().trim().max(40).optional(),
	company: z.string().trim().max(120).optional(),
	jobTitle: z.string().trim().max(120).optional(),
	notes: z.string().trim().max(2000).optional(),
});
export type TCreateExperienceLeadDto = z.infer<typeof CreateExperienceLeadDto>;
